import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { getRememberVisitorValue, useStorage } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { A11yState } from '../../types';
import { useModeData } from '../data/use-mode-data';
import {
  A11yStateContext,
  type A11yStateContextProps
} from '../hooks/use-a11y-state';
import { useApplyInlineStyles } from '../hooks/use-apply-inline-styles';
import { useCreateStyle } from '../hooks/use-create-style';
import { useFocusFirstVisibleElement } from '../hooks/use-focus-first-visible-element';
import { useTextStyleAttributes } from '../hooks/use-text-style-attributes';

const LOCAL_STORAGE_KEY = 'eapps-accessibility-settings';

const DEFAULT_STATE: A11yState = {
  colorsContrast: 'default-colors'
};

type A11yStateEntry = [keyof A11yState, A11yState[keyof A11yState]];

export function A11yStateProvider({ children }: PropsWithChildren) {
  const { rememberVisitor } = useSettings();
  const modeData = useModeData();

  const expiresDate = getRememberVisitorValue(rememberVisitor);

  const [storedA11yData, { set: setA11yStorage, isExpired }] =
    useStorage<A11yState>(LOCAL_STORAGE_KEY, DEFAULT_STATE);

  const setA11yState = useCallback(
    (state: A11yState) => {
      const newState = {
        ...storedA11yData,
        ...state
      };

      (Object.entries(state) as A11yStateEntry[]).forEach(([key, value]) => {
        if (value === false) {
          delete newState[key];
        }
      });

      setA11yStorage(newState);
    },
    [storedA11yData, setA11yStorage]
  );

  const setA11yMode: A11yStateContextProps['setA11yMode'] = useCallback(
    (mode, settings) => {
      setA11yStorage({
        ...DEFAULT_STATE,
        ...settings,
        mode
      });
    },
    [setA11yStorage]
  );

  const resetA11yState = useCallback(() => {
    setA11yStorage(DEFAULT_STATE);
  }, [setA11yStorage]);

  const isA11yStateModified = useMemo(() => {
    if (!storedA11yData) {
      return false;
    }

    return (Object.entries(storedA11yData) as A11yStateEntry[]).some(
      ([key, value]) => isValueModified(key, value)
    );
  }, [storedA11yData]);

  const modifiedSettingsCount = useMemo(() => {
    if (!storedA11yData) {
      return 0;
    }

    const currentMode = storedA11yData.mode;

    // If no mode is selected, consider all changes from DEFAULT_STATE as manual
    if (!currentMode) {
      return (Object.entries(storedA11yData) as A11yStateEntry[]).reduce(
        (count, [key, value]) => {
          if (key === 'mode') {
            return count;
          }

          return isValueModified(key, value) ? count + 1 : count;
        },
        0
      );
    }

    const modeSettings = modeData[currentMode]?.settings || {};

    let count = 0;

    // Check all settings in the current state
    (Object.entries(storedA11yData) as A11yStateEntry[]).forEach(
      ([key, value]) => {
        if (key === 'mode') {
          return;
        }

        const modeValue = modeSettings[key as keyof typeof modeSettings];

        // If the setting differs from what the mode provides, count it as a manual change
        if (modeValue !== undefined) {
          if (value !== modeValue) {
            count++;
          }
        } else {
          if (isValueModified(key as keyof A11yState, value)) {
            count++;
          }
        }
      }
    );

    return count;
  }, [storedA11yData, modeData]);

  useEffect(() => {
    if (isExpired(expiresDate)) {
      resetA11yState();
    }
  }, [expiresDate]);

  const a11yState = useMemo(
    () => storedA11yData || DEFAULT_STATE,
    [storedA11yData]
  );

  const processedElements = useTextStyleAttributes();
  useApplyInlineStyles(a11yState, processedElements);
  useFocusFirstVisibleElement(a11yState);
  useCreateStyle(a11yState);

  return (
    <A11yStateContext.Provider
      value={{
        a11yState,
        isA11yStateModified,
        modifiedSettingsCount,
        setA11yState,
        setA11yMode,
        resetA11yState
      }}
    >
      {children}
    </A11yStateContext.Provider>
  );
}

function isValueModified(
  key: keyof A11yState,
  value: A11yState[keyof A11yState]
) {
  return value !== DEFAULT_STATE[key];
}
